import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MEDIA_TYPE } from '../../../../../shared/constants/common';
import { formatDuration } from '../../../../../shared/utils/function';
import { calculatePriceWithMarkup } from '../../../../../shared/utils/paymentUtil';
import {
  isSignableFileKey,
  resolveSignedUrlMap,
  SIGNED_URL_REFRESH_EVENT,
  toDurableFileKey,
} from '../../../../../shared/utils/signedUrlCache';
import { captureChatException } from 'shared/sentry';
import { checkContentPurchase } from '../../../../../shared/actions/purchaseAction';
import { renderTextWithLinks } from '../../../../../shared/utils/chatUtil';
import { attachHorizontalSwipeGesture } from '../../../../../shared/utils/horizontalSwipeGesture';
import LockedPPVContentPreview from '../../../../../features/cards/user/LockedPPVContentPreview';
import BundleSlider from '../../../../../features/slider/BundleSlider';
import '../../../../../features/cards/css/user/feed.css';
import VideoPlayer from '../../../../../shared/components/VideoPlayer';
import ChatImagePreview from './ChatImagePreview';

function parseMediaDuration(value) {
  if (value == null || value === '') return null;
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
}

function normalizeChatAssetType(asset) {
  const raw = asset?.type;
  if (typeof raw !== 'string' || !raw.trim()) return MEDIA_TYPE.IMAGE;
  const s = raw.trim().toLowerCase();
  if (s === MEDIA_TYPE.VIDEO) return MEDIA_TYPE.VIDEO;
  return MEDIA_TYPE.IMAGE;
}

function collectAssetKeys(assets) {
  const keys = [];
  (assets || []).forEach((asset) => {
    const mainKey = toDurableFileKey(asset?.key) || (isSignableFileKey(asset?.key) ? asset.key.trim() : null);
    const posterKey =
      toDurableFileKey(asset?.poster) ||
      (isSignableFileKey(asset?.poster) ? String(asset.poster).trim() : null);

    if (mainKey && isSignableFileKey(mainKey)) keys.push(mainKey);
    if (posterKey && isSignableFileKey(posterKey)) keys.push(posterKey);
  });
  return [...new Set(keys)];
}

const ChatMediaMessage = ({
  parsedMediaJson,
  isUser,
  otherAvatar,
  creatorInfo: creatorInfoProp,
  messageEventId = null,
  messagePurchaseContentIds = [],
  onEditMessage = null,
}) => {
  const [signedUrlMap, setSignedUrlMap] = useState({});
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const [shouldMountVideo, setShouldMountVideo] = useState(false);
  const [chatVideoDuration, setChatVideoDuration] = useState(null);
  const [hasChatVideoStarted, setHasChatVideoStarted] = useState(false);
  const [isChatVideoPlaying, setIsChatVideoPlaying] = useState(false);
  const [bundleSlideIndex, setBundleSlideIndex] = useState(0);
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const containerRef = useRef(null);
  const mediaInnerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: '100px', threshold: 0.01 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => setIsImagePreviewOpen(false);
  }, []);

  const refreshSignedUrls = useCallback(async (force = false) => {
    const assets = parsedMediaJson?.assets;
    if (!Array.isArray(assets) || !assets.length) return;

    const uniqueKeys = collectAssetKeys(assets);
    if (!uniqueKeys.length) return;

    try {
      const nextMap = await resolveSignedUrlMap(uniqueKeys, { force, isProfile: false });
      setSignedUrlMap((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.keys(nextMap).forEach((key) => {
          if (next[key] !== nextMap[key]) {
            next[key] = nextMap[key];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    } catch (error) {
      captureChatException(error, { tags: { feature: 'chat', action: 'signed-urls' } });
    }
  }, [parsedMediaJson]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      await refreshSignedUrls(false);
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshSignedUrls]);

  // Refresh in place before/at expiry (no full page reload) when tab becomes visible
  // or when the app-level signed-URL refresh event fires after a long background.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshSignedUrls(false);
      }
    };
    const onRefreshEvent = () => {
      refreshSignedUrls(true);
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(SIGNED_URL_REFRESH_EVENT, onRefreshEvent);

    // Keep URLs fresh while the chat stays open past the ~1h signed-URL lifetime.
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        refreshSignedUrls(false);
      }
    }, 5 * 60 * 1000);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(SIGNED_URL_REFRESH_EVENT, onRefreshEvent);
      window.clearInterval(intervalId);
    };
  }, [refreshSignedUrls]);

  const resolveMediaUrl = useCallback((originalKey) => {
    if (typeof originalKey !== 'string' || !originalKey.trim()) {
      return null;
    }

    const durableKey = toDurableFileKey(originalKey);
    if (durableKey && isSignableFileKey(durableKey)) {
      // Prefer a freshly signed URL; fall back to embedded http while signing loads.
      return signedUrlMap[durableKey] || (originalKey.startsWith('http') ? originalKey : null);
    }

    if (originalKey.startsWith('http')) {
      return originalKey;
    }

    return signedUrlMap[originalKey] || null;
  }, [signedUrlMap]);

  const videoSignRetryRef = useRef(0);

  const handleVideoLoadError = useCallback(() => {
    // Expired or broken signed URL — force a fresh sign and remount via src change.
    if (videoSignRetryRef.current >= 2) return;
    videoSignRetryRef.current += 1;
    refreshSignedUrls(true);
  }, [refreshSignedUrls]);

  useEffect(() => {
    videoSignRetryRef.current = 0;
  }, [parsedMediaJson]);

  const mediaRenderData = useMemo(() => {
    if (!parsedMediaJson) return null;

    const assets = Array.isArray(parsedMediaJson.assets) ? parsedMediaJson.assets : [];
    if (!assets.length) return null;

    const first = assets[0];
    const isBundle = assets.length > 1;

    const mediaArray = assets.map((asset) => ({
      src: resolveMediaUrl(asset.key),
      type_file: normalizeChatAssetType(asset),
      duration: asset.duration,
      poster: resolveMediaUrl(asset.poster),
    }));

    const rawPriceValue =
      parsedMediaJson.price ??
      parsedMediaJson.real_price ??
      parsedMediaJson.baseAmount;

    const numericPrice = Number.isFinite(Number(rawPriceValue)) ? Number(rawPriceValue) : 0;

    const contentId = parsedMediaJson?.content_id;
    const purchasedViaRoomInfo =
      Array.isArray(messagePurchaseContentIds) &&
      contentId != null &&
      messagePurchaseContentIds.some((id) => String(id) === String(contentId));
    // After checkout the timeline often updates `lock_content` before Redux room metadata
    // includes the new purchased id — use both so badges and icons stay in sync without refresh.
    const unlockedViaSyncedMessageBody = parsedMediaJson?.lock_content === false;
    const isPaidContentAccessible = purchasedViaRoomInfo || unlockedViaSyncedMessageBody;
    const lockContent = isPaidContentAccessible ? false : Boolean(parsedMediaJson.lock_content);

    const calculatedPrice = calculatePriceWithMarkup(numericPrice);

    const post = {
      _id: parsedMediaJson.content_id || null,
      id: parsedMediaJson.content_id || null,
      price: Number.isFinite(calculatedPrice) ? calculatedPrice : 0,
      real_price: Number.isFinite(numericPrice) ? numericPrice : 0,
      caption: parsedMediaJson.caption || '',
      type_file: isBundle ? MEDIA_TYPE.BUNDLE : first?.type || null,
      duration: !isBundle ? (first?.duration ?? null) : null,
      assets: mediaArray.map((m) => ({
        key: m.src,
        type: m.type_file,
        duration: m.duration,
        poster: m.poster,
      })),
    };

    return {
      isBundle,
      mediaArray,
      post,
      lockContent,
      isPaidContentAccessible,
      caption: parsedMediaJson.caption || '',
    };
  }, [
    parsedMediaJson,
    messagePurchaseContentIds,
    resolveMediaUrl,
  ]);

  useEffect(() => {
    if (!mediaRenderData || !Array.isArray(mediaRenderData.mediaArray)) {
      return;
    }
    setActiveMediaIndex(0);
    setIsImagePreviewOpen(false);
    setIsImageLoaded(false);
  }, [mediaRenderData?.mediaArray?.length || 0]);

  useEffect(() => {
    setIsImageLoaded(false);
  }, [activeMediaIndex]);

  useEffect(() => {
    setHasChatVideoStarted(false);
    setIsChatVideoPlaying(false);
    const arr = mediaRenderData?.mediaArray;
    const current = arr?.[activeMediaIndex] || arr?.[0];
    setChatVideoDuration(parseMediaDuration(current?.duration));
  }, [activeMediaIndex, mediaRenderData]);

  useEffect(() => {
    let timeoutId;

    if (inView) {
      timeoutId = setTimeout(() => setShouldMountVideo(true), 120);
    } else if (!hasChatVideoStarted) {
      // Keep mounted after first play so Safari preserves paused native controls on scroll-back.
      setShouldMountVideo(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [inView, hasChatVideoStarted]);

  const primary = useMemo(
    () => {
      if (!mediaRenderData?.mediaArray?.length) return null;
      const arr = mediaRenderData.mediaArray;
      return arr[activeMediaIndex] || arr[0] || null;
    },
    [mediaRenderData, activeMediaIndex]
  );

  // Use BundleSlider whenever media is shown (not the purchase overlay). Do not gate on
  // lockContent — paid bundles set lock_content for PPV but creators/subscribers still see
  // assets; the fallback single-video + external-swipe path breaks horizontal swipes on video.
  const canShowBundleMedia = useMemo(() => {
    if (!mediaRenderData) return false;
    return !(isUser && mediaRenderData.lockContent);
  }, [mediaRenderData, isUser]);

  const showChatBundleCarousel = useMemo(() => {
    if (!mediaRenderData || !canShowBundleMedia) return false;
    const { isBundle, mediaArray } = mediaRenderData;
    return (
      isBundle &&
      mediaArray.length > 1 &&
      mediaArray.every((m) => m?.src)
    );
  }, [mediaRenderData, canShowBundleMedia]);

  const showExternalPagination = useMemo(() => {
    if (!mediaRenderData) return false;
    const { mediaArray, lockContent } = mediaRenderData;
    return (
      mediaArray.length > 1 &&
      ((isUser && !lockContent) || !isUser) &&
      !showChatBundleCarousel
    );
  }, [mediaRenderData, isUser, showChatBundleCarousel]);

  useEffect(() => {
    if (!showExternalPagination) return undefined;

    const el = mediaInnerRef.current;
    if (!el) return undefined;

    const mediaCount = mediaRenderData?.mediaArray?.length ?? 0;

    return attachHorizontalSwipeGesture(el, {
      onSwipeLeft: () => {
        setActiveMediaIndex((index) =>
          index >= mediaCount - 1 ? index : index + 1
        );
      },
      onSwipeRight: () => {
        setActiveMediaIndex((index) => (index <= 0 ? index : index - 1));
      },
      isInteractiveTarget: (target) =>
        Boolean(
          target?.closest?.(
            'button, a, input, textarea, select, [role="button"], .cm_chat_media_pagination, .cm_chat_media_pagination_dot'
          )
        ),
      useCapture: true,
      chatMode: true,
    });
  }, [showExternalPagination, mediaRenderData?.mediaArray?.length]);

  if (!mediaRenderData || !primary) {
    return null;
  }

  const { isBundle, mediaArray, post, lockContent, isPaidContentAccessible, caption } = mediaRenderData;

  const bundleCarouselPending =
    isBundle &&
    mediaArray.length > 1 &&
    canShowBundleMedia &&
    !mediaArray.every((m) => m?.src);

  const isImage = primary.type_file === MEDIA_TYPE.IMAGE;
  const isVideo = primary.type_file === MEDIA_TYPE.VIDEO;

  const shouldShowPremiumBadges =
    !isUser && Boolean(creatorInfoProp) && Number(post?.price) > 0 && !isChatVideoPlaying;

  const premiumLabel = isPaidContentAccessible ? 'Sold' : 'Premium';
  const premiumIconClass = isPaidContentAccessible ? 'fa-lock-open' : 'fa-lock';
  const premiumPriceLabel = Number(post?.real_price || 0).toFixed(2);

  const openImagePreview = (index = showChatBundleCarousel ? bundleSlideIndex : activeMediaIndex) => {
    // lock_content stays true for PPV metadata even when the creator (or purchaser) can view media.
    if (!canShowBundleMedia) return;

    const media = mediaArray[index];
    if (!media?.src || media.type_file !== MEDIA_TYPE.IMAGE) return;

    if (typeof Image !== 'undefined') {
      const img = new Image();
      img.src = media.src;
    }

    setPreviewImageIndex(index);
    setIsImagePreviewOpen(true);
  };

  const previewImageSrc = mediaArray[previewImageIndex]?.src || null;

  return (
    <div
      className={`cm_chat_media${showChatBundleCarousel ? ' cm_chat_media_bundle' : ''}`}
      ref={containerRef}
    >
      <div
        ref={mediaInnerRef}
        className={`cm_chat_media_inner${showExternalPagination ? ' cm_chat_media_inner_swipeable' : ''}${isChatVideoPlaying ? ' cm_chat_media_video_playing' : ''}`}
      >
        {shouldShowPremiumBadges && (
          <>
            <div className={`content-type-badge ${isPaidContentAccessible ? 'sold' : 'premium'}`}>
              {premiumLabel}
            </div>
            <div className="price-badge">
              <i className={`fa-solid ${premiumIconClass}`} />
              ${premiumPriceLabel}
            </div>
          </>
        )}
        {(isUser && lockContent) ? (
          <LockedPPVContentPreview
            purchase_type='message_content'
            isBundle={isBundle}
            mediaArray={mediaArray}
            post={post}
            creatorInfo={
              creatorInfoProp
                ? { ...creatorInfoProp, profile_picture: creatorInfoProp.profile_picture || otherAvatar }
                : { profile_picture: otherAvatar }
            }
            vatPercentage={0}
            onUnlockClick={
              onEditMessage && messageEventId && parsedMediaJson
                ? async () => {
                  const contentId = parsedMediaJson?.content_id;
                  if (!contentId) return;

                  const result = await checkContentPurchase(contentId);
                  if (!result.success) {
                    return;
                  }
                  if (result.purchased) {
                    const next = { ...parsedMediaJson, lock_content: false };
                    onEditMessage(messageEventId, JSON.stringify(next));
                  }
                }
                : undefined
            }
          />
        ) : (
          <>
            {showChatBundleCarousel && (
              <BundleSlider
                media={mediaArray}
                showControls
                isShowDuration={true}
                chatNativeVideoChrome={false}
                onMessagingVideoPlayingChange={setIsChatVideoPlaying}
                onCurrentIndexChange={setBundleSlideIndex}
                onImageClick={(index) => openImagePreview(index)}
                dotsPlacement="overlay"
                className="cm_chat_bundle_slider"
              />
            )}
            {bundleCarouselPending && (
              <div className="cm_chat_media_loading" aria-hidden />
            )}
            {!showChatBundleCarousel && !bundleCarouselPending && isImage && primary.src && (
              <>
                {!inView ? (
                  <div className="cm_chat_media_loading" aria-hidden />
                ) : (
                  <>
                    {!isImageLoaded && (
                      <div className="cm_chat_media_loading" aria-hidden />
                    )}
                    <button
                      type="button"
                      className="cm_chat_media_img_button"
                      onClick={() => openImagePreview(activeMediaIndex)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          openImagePreview(activeMediaIndex);
                        }
                      }}
                      aria-label="View image"
                      tabIndex={0}
                    >
                      <img
                        src={primary.src}
                        alt={caption || 'Chat media'}
                        className="cm_chat_media_img"
                        style={{ opacity: isImageLoaded ? 1 : 0 }}
                        loading="lazy"
                        decoding="async"
                        onLoad={() => setIsImageLoaded(true)}
                      />
                    </button>

                  </>
                )}
              </>
            )}
            {canShowBundleMedia && isImagePreviewOpen && previewImageSrc && (
              <ChatImagePreview
                src={previewImageSrc}
                alt={caption || 'Chat media'}
                onClose={() => setIsImagePreviewOpen(false)}
              />
            )}
            {!showChatBundleCarousel && !bundleCarouselPending && isVideo && primary.src && (
              shouldMountVideo ? (
                <>
                  <VideoPlayer
                    src={primary.src}
                    poster={primary.poster || null}
                    className="cm_chat_media_video"
                    showControls
                    messagingChromeSync
                    controlsList="nodownload noplaybackrate noremoteplayback"
                    disablePictureInPicture
                    disableRemotePlayback
                    preventContextMenu
                    muted
                    playsInline
                    preload="metadata"
                    fallbackText="Video unavailable"
                    pauseWhenOutOfView
                    playbackStarted={hasChatVideoStarted}
                    onError={handleVideoLoadError}
                    onPlayStarted={() => {
                      setHasChatVideoStarted(true);
                      setIsChatVideoPlaying(true);
                    }}
                    onPause={() => setIsChatVideoPlaying(false)}
                    onScrollPause={() => {
                      setHasChatVideoStarted(false);
                      setIsChatVideoPlaying(false);
                    }}
                    onDurationReady={(seconds) => {
                      setChatVideoDuration((prev) => prev ?? parseMediaDuration(seconds));
                    }}
                  />
                  {chatVideoDuration && !hasChatVideoStarted ? (
                    <div
                      className="video_time_badge cm_chat_media_duration_badge"
                      aria-hidden
                    >
                      <i className="fa-solid fa-video" />
                      {formatDuration(chatVideoDuration)}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="cm_chat_media_loading" aria-hidden />
              )
            )}
            {!showChatBundleCarousel && !bundleCarouselPending && !isImage && !isVideo && (
              <div className="cm_chat_media_placeholder">
                <i className="fa-solid fa-file" aria-hidden />
              </div>
            )}
          </>
        )}
      </div>
      {caption ? (
        <p className="cm_chat_media_caption">
          {renderTextWithLinks(caption)}
        </p>
      ) : null}
      {showExternalPagination && (
        <div className="cm_chat_media_pagination" aria-label="Media pagination">
          {mediaArray.map((media, index) => (
            <button
              key={media.src || index}
              type="button"
              className={`cm_chat_media_pagination_dot${index === activeMediaIndex ? ' cm_chat_media_pagination_dot_active' : ''
                }`}
              onClick={() => setActiveMediaIndex(index)}
              aria-label={`Show media ${index + 1}`}
              aria-pressed={index === activeMediaIndex}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(ChatMediaMessage);

